<template>
    <div class="emission-wrap">
        <div class="emission-head-row">
            <span class="dash-panel-eyebrow">사업장별 배출</span>
            <div class="emission-tabs">
                <button
                    :class="['emission-tab', activeTab === 'rank' && 'emission-tab--on']"
                    @click="activeTab = 'rank'"
                >
                    순위
                </button>
                <button
                    :class="['emission-tab', activeTab === 'quad' && 'emission-tab--on']"
                    @click="activeTab = 'quad'"
                >
                    분포
                </button>
            </div>
            <span class="dash-panel-eyebrow-meta num">총 {{ facilities.length }}곳</span>
        </div>

        <FacilityRankTab v-if="activeTab === 'rank'" :sorted="sorted" :api-unit="apiUnit" :display-unit="displayUnit" />
        <FacilityDistributionTab v-else :enriched="enriched" :api-unit="apiUnit" :display-unit="displayUnit" />

        <div class="emission-foot">
            <template v-if="activeTab === 'rank' && top5.length">
                <span class="emission-foot-badge">
                    <span class="emission-foot-rank">1위</span>
                    <span class="emission-foot-name">{{ top5[0].name }}</span>
                    <span class="emission-foot-sep">·</span>
                    <span class="emission-foot-pct num">{{ (top5[0].ratio ?? 0).toFixed(1) }}%</span>
                </span>
            </template>
            <template v-else-if="activeTab === 'quad'">
                <span class="emission-leg"
                    ><span class="emission-leg-sw" style="background: #b88330"></span>Scope 1</span
                >
                <span class="emission-leg"
                    ><span class="emission-leg-sw" style="background: #1e7a63"></span>Scope 2</span
                >
                <span class="emission-leg"
                    ><span class="emission-leg-sw" style="background: #3a7fc1"></span>Scope 3</span
                >
                <span class="emission-leg-sep">·</span>
                <span class="emission-leg-note">점 크기 = 배출량</span>
            </template>
        </div>
    </div>
</template>

<script>
import FacilityRankTab from "./FacilityRankTab.vue";
import FacilityDistributionTab from "./FacilityDistributionTab.vue";

export default {
    name: "FacilityEmissionPanel",
    components: { FacilityRankTab, FacilityDistributionTab },
    props: {
        facilities: { type: Array, default: () => [] },
        previousFacilities: { type: Array, default: () => [] },
        apiUnit: { type: String, default: "kgCO2e" },
        displayUnit: { type: String, default: "kgCO2e" },
        yoyPercent: { type: Number, default: null },
    },
    data() {
        return { activeTab: "rank" };
    },
    computed: {
        prevMap() {
            const m = {};
            for (const f of this.previousFacilities) {
                m[f.facilityCode ?? f.code] = f;
            }
            return m;
        },
        enriched() {
            return this.facilities.map((f) => {
                const code = f.facilityCode ?? f.code;
                const prev = this.prevMap[code];
                const yoy = prev && prev.total > 0 ? ((f.total - prev.total) / prev.total) * 100 : null;
                const scope = (f.scope2 ?? 0) >= (f.scope1 ?? 0) ? 2 : 1;
                return { ...f, code, yoy, isNew: !prev, scope };
            });
        },
        sorted() {
            return [...this.enriched].sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
        },
        top5() {
            return this.sorted.slice(0, 5);
        },
    },
};
</script>

<style scoped>
.emission-wrap {
    background: var(--dash-surface);
    border: 1px solid var(--dash-hairline-soft);
    border-radius: 14px;
    padding: 14px 18px 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    color: var(--dash-ink);
}
.num {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
}

/* ── header ── */
.emission-head-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    flex-shrink: 0;
}

/* ── tabs ── */
.emission-tabs {
    display: flex;
    background: var(--dash-surface-2);
    border-radius: 999px;
    padding: 2px;
    gap: 1px;
}
.emission-tab {
    padding: 3px 11px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: var(--dash-ink-mute);
    background: transparent;
    border: none;
    cursor: pointer;
    transition:
        color 0.12s,
        background 0.12s,
        box-shadow 0.12s;
    letter-spacing: 0.02em;
}
.emission-tab:focus {
    outline: none;
}
.emission-tab:hover {
    color: var(--dash-ink);
}
.emission-tab--on {
    background: var(--dash-surface);
    color: var(--dash-ink);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* ── footer ── */
.emission-foot {
    flex-shrink: 0;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--dash-hairline-soft);
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 10px;
    color: var(--dash-ink-mute);
    min-height: 24px;
}

.emission-foot-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: var(--dash-accent-wash);
    border-radius: 999px;
    padding: 3px 10px;
}
.emission-foot-rank {
    font-size: 9px;
    font-weight: 800;
    color: #fff;
    background: var(--dash-accent);
    border-radius: 999px;
    padding: 1px 5px;
    flex-shrink: 0;
    letter-spacing: 0.04em;
}
.emission-foot-name {
    font-size: 11px;
    font-weight: 600;
    color: var(--dash-accent);
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 120px;
}
.emission-foot-sep {
    color: var(--dash-accent-tint);
    font-size: 11px;
    flex-shrink: 0;
}
.emission-foot-pct {
    font-size: 11px;
    font-weight: 800;
    color: var(--dash-accent);
    flex-shrink: 0;
}

.emission-leg {
    display: flex;
    align-items: center;
    gap: 4px;
}
.emission-leg-sw {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}
.emission-leg-sep {
    color: var(--dash-hairline);
}
.emission-leg-note {
    color: var(--dash-ink-faint);
    font-size: 9.5px;
}
</style>
